import { forwardRef } from 'react'
import AlertTriangle from '@untitled-ui/icons-react/build/cjs/AlertTriangle'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import {
	AppButton,
	Modal,
	type ConfirmDialogProps
} from '@clickguard/design-system'

// TODO: Remove this local component once the design-system ConfirmDialog supports
// loader="dots". Restore consumer imports to @clickguard/design-system and pass
// loader="dots" there, then remove or migrate this component's tests.
// Keep the design-system layout and confirmation behavior; only the loader differs.
const TONES = {
	brand: {
		ring: 'cg-bg-brand-tint',
		icon: 'cg-text-brand',
		confirmVariant: 'primary' as const
	},
	danger: {
		ring: 'cg-bg-danger-50',
		icon: 'cg-text-danger',
		confirmVariant: 'destructive' as const
	}
}

const ConfirmDialog = forwardRef<HTMLDivElement, ConfirmDialogProps>(
	(
		{
			open,
			onClose,
			onConfirm,
			title,
			description,
			confirmLabel = 'Confirm',
			cancelLabel = 'Cancel',
			tone = 'brand',
			icon,
			loading = false,
			closeOnConfirm = true,
			hideCancel = false,
			size = 'sm',
			className,
			overlayClassName,
			zIndex
		},
		ref
	) => {
		const toneStyle = TONES[tone]

		const handleConfirm = async () => {
			await onConfirm()
			if (closeOnConfirm) onClose()
		}

		return (
			<Modal
				ref={ref}
				open={open}
				onClose={onClose}
				size={size}
				rounded="modal"
				className={twMerge(clsx('cg-p-6', className))}
				overlayClassName={overlayClassName}
				zIndex={zIndex}
			>
				<div className="cg-flex cg-flex-col cg-items-center cg-gap-4 cg-text-center">
					<span
						className={clsx(
							'cg-flex cg-size-12 cg-items-center cg-justify-center cg-rounded-full',
							toneStyle.ring
						)}
						aria-hidden="true"
					>
						<span className={clsx('cg-inline-flex cg-size-6', toneStyle.icon)}>
							{icon ?? <AlertTriangle className="cg-size-6" />}
						</span>
					</span>

					<div className="cg-flex cg-flex-col cg-gap-1">
						<h2 className="cg-font-sans cg-text-text-lg cg-font-semibold cg-text-text-primary">
							{title}
						</h2>
						{description && (
							<p className="cg-font-sans cg-text-text-sm cg-text-text-secondary">
								{description}
							</p>
						)}
					</div>
					<div className="cg-mt-2 cg-flex cg-w-full cg-justify-end cg-gap-3">
						{!hideCancel && (
							<AppButton
								variant="secondary"
								size="md"
								label={cancelLabel}
								onClick={onClose}
								disabled={loading}
							/>
						)}
						<AppButton
							variant={toneStyle.confirmVariant}
							size="md"
							label={confirmLabel}
							isLoading={loading}
							loader="dots"
							onClick={handleConfirm}
						/>
					</div>
				</div>
			</Modal>
		)
	}
)

ConfirmDialog.displayName = 'ConfirmDialog'

export { ConfirmDialog }
